import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, DatePicker, message, Popconfirm, Space, Table } from "antd";
import { CheckSquareOutlined, BorderOutlined, SwapOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { serverFetch } from "../../server";
import { fmtDate, strDate2Server } from "../../extensions/utils";
import { getTableColumn } from "../../extensions/helper";
import { BtnSettingsTable, tblGetColumns } from "../../controls/table-settings.js/table-settings";
import { SportTooltip } from "../../controls";
import ServiceMonitoringAbonModal from "./service-monitoring-abon-modal";
import "./service-monitoring.css";

const tableName = "service-monitoring";
const periodStorageKey = "service-monitoring-period";

const loadPeriod = () => {
  try {
    const raw = localStorage.getItem(periodStorageKey);
    if (!raw) return { dateFrom: null, dateTo: null };
    const parsed = JSON.parse(raw);
    return {
      dateFrom: parsed.dateFrom ? dayjs(parsed.dateFrom) : null,
      dateTo: parsed.dateTo ? dayjs(parsed.dateTo) : null,
    };
  } catch {
    return { dateFrom: null, dateTo: null };
  }
};

const ServiceMonitoring = () => {
  const savedPeriod = loadPeriod();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [dateFrom, setDateFrom] = useState(savedPeriod.dateFrom);
  const [dateTo, setDateTo] = useState(savedPeriod.dateTo);
  const [editId, setEditId] = useState(null);
  const [checkedOnce, setCheckedOnce] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const [tableHeight, setTableHeight] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);
  const divRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(
      periodStorageKey,
      JSON.stringify({
        dateFrom: dateFrom ? strDate2Server(dateFrom) : null,
        dateTo: dateTo ? strDate2Server(dateTo) : null,
      })
    );
  }, [dateFrom, dateTo]);

  const updateTableSize = () => {
    if (!divRef.current) return;
    const headerSize = divRef.current.querySelector(".ant-table-thead")?.offsetHeight ?? 39;
    const top = divRef.current.getBoundingClientRect().top;
    const height = document.documentElement.offsetHeight - headerSize - top - window.scrollY - 8;
    const width = document.documentElement.offsetWidth - divRef.current.getBoundingClientRect().left;
    setTableHeight(height > 0 ? height : 0);
    setTableWidth(width > 0 ? width : 0);
  };

  useLayoutEffect(() => {
    updateTableSize();
    window.addEventListener("resize", updateTableSize);
    return () => window.removeEventListener("resize", updateTableSize);
  }, [items, tableKey, checkedOnce, loading]);

  const removeItems = (ids) => {
    const idSet = new Set(ids);
    setItems((prev) => prev.filter((item) => !idSet.has(item.id)));
    setSelected((prev) => prev.filter((id) => !idSet.has(id)));
  };

  const onTableSettingsChange = () => {
    setTableKey((prev) => prev + 1);
  };

  const onRunCheck = () => {
    setLoading(true);
    const queryParams = {};
    if (dateFrom) queryParams.dateFrom = strDate2Server(dateFrom);
    if (dateTo) queryParams.dateTo = strDate2Server(dateTo);

    return serverFetch("Sale/AbonMonitor", { queryParams })
      .then((data) => {
        const list = data?.items || data?.Items || [];
        const count = data?.count ?? data?.Count ?? list.length;
        setItems(list);
        setSelected([]);
        setCheckedOnce(true);
        message.info(
          count > 0
            ? `Найдено некорректных записей: ${count}`
            : "Некорректных записей не найдено"
        );
      })
      .catch((e) => message.error(e.userMessage || "Ошибка проверки"))
      .finally(() => setLoading(false));
  };

  const onApply = () => {
    if (selected.length === 0) return;
    setLoading(true);
    serverFetch("Sale/MonitorApply", { method: "POST", bodyData: selected })
      .then((data) => {
        message.success(data?.message || data?.Message || "Изменения применены");
        removeItems(selected);
        return onRunCheck();
      })
      .catch((e) => message.error(e.userMessage || "Ошибка применения изменений"))
      .finally(() => setLoading(false));
  };

  const onCancelSelected = () => {
    if (selected.length === 0) return;
    removeItems(selected);
    message.success("Выбранные записи убраны из списка");
  };

  const onSelectAll = () => setSelected(items.map((item) => item.id));
  const onClearSelection = () => setSelected([]);
  const onInvertSelection = () => {
    const selectedSet = new Set(selected);
    setSelected(items.filter((item) => !selectedSet.has(item.id)).map((item) => item.id));
  };

  const renderClamp = (value) => (
    <div className="service-monitoring-cell-clamp" title={value || undefined}>
      {value || "—"}
    </div>
  );

  const columns = [
    getTableColumn("id", "ID", { type: "number", width: 80, isSorter: true }, items),
    getTableColumn("name", "Название абонемента", { width: 180, isSorter: true, render: renderClamp }, items),
    getTableColumn("saleDate", "Дата продажи", {
      type: "date",
      width: 120,
      isSorter: true,
      render: (v) => fmtDate(v) || "—",
    }, items),
    getTableColumn("activationDate", "Дата активации", {
      type: "date",
      width: 120,
      isSorter: true,
      render: (v) => fmtDate(v) || "—",
    }, items),
    getTableColumn("closeDate", "Дата закрытия", {
      type: "date",
      width: 120,
      isSorter: true,
      render: (v) => fmtDate(v) || "—",
    }, items),
    getTableColumn("reason", "Причина", { width: 220, render: renderClamp }, items),
    getTableColumn("client", "Клиент", { width: 160, isSorter: true, render: renderClamp }, items),
    getTableColumn("manager", "Менеджер", { width: 140, isSorter: true, render: renderClamp }, items),
    getTableColumn("comment", "Комментарий", { width: 180, render: renderClamp }, items),
    getTableColumn("systemCloseDate", "Фактическая дата закрытия (система)", {
      type: "date",
      width: 160,
      isSorter: true,
      render: (v) => fmtDate(v) || "—",
    }, items),
  ];

  const hasSelection = selected.length > 0;

  return (
    <div className="service-monitoring">
      <div className="service-monitoring-toolbar">
        <Space wrap>
          <DatePicker
            value={dateFrom}
            format="DD.MM.YYYY"
            placeholder="Период с"
            onChange={setDateFrom}
            allowClear
          />
          <DatePicker
            value={dateTo}
            format="DD.MM.YYYY"
            placeholder="Период по"
            onChange={setDateTo}
            allowClear
            disabledDate={(current) => dateFrom && current && current.isBefore(dateFrom, "day")}
          />
          <SportTooltip title="Запустить проверку движений статусов">
            <span>
              <Button type="primary" onClick={onRunCheck} disabled={loading}>
                Проверить
              </Button>
            </span>
          </SportTooltip>
          <SportTooltip title="Принять изменения системы">
            <span>
              <Popconfirm
                title="Подтверждение"
                description="Принять системные даты закрытия для выбранных абонементов?"
                onConfirm={onApply}
                disabled={!hasSelection || loading}
              >
                <Button type="primary" disabled={!hasSelection || loading}>
                  Принять
                </Button>
              </Popconfirm>
            </span>
          </SportTooltip>
          <SportTooltip title="Убрать выбранные из списка без исправления">
            <span>
              <Popconfirm
                title="Подтверждение"
                description="Убрать выбранные абонементы из списка без исправления?"
                onConfirm={onCancelSelected}
                disabled={!hasSelection || loading}
              >
                <Button disabled={!hasSelection || loading}>Отменить</Button>
              </Popconfirm>
            </span>
          </SportTooltip>
          <SportTooltip title="Выделить все">
            <span>
              <Button
                icon={<CheckSquareOutlined />}
                onClick={onSelectAll}
                disabled={items.length === 0 || loading}
              />
            </span>
          </SportTooltip>
          <SportTooltip title="Инвертировать">
            <span>
              <Button
                icon={<SwapOutlined />}
                onClick={onInvertSelection}
                disabled={items.length === 0 || loading}
              />
            </span>
          </SportTooltip>
          <SportTooltip title="Снять выделение">
            <span>
              <Button
                icon={<BorderOutlined />}
                onClick={onClearSelection}
                disabled={!hasSelection || loading}
              />
            </span>
          </SportTooltip>
          <BtnSettingsTable name={tableName} columns={columns} onSettingsChange={onTableSettingsChange} />
        </Space>
      </div>
      {checkedOnce && (
        <div className="service-monitoring-count">
          Записей: {items.length}
          {hasSelection ? `, выбрано: ${selected.length}` : ""}
        </div>
      )}

      <div ref={divRef} className="service-monitoring-table" style={{ width: tableWidth || "100%" }}>
        <Table
          key={tableKey}
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={items}
          columns={tblGetColumns(columns, tableName)}
          pagination={false}
          scroll={{ y: tableHeight || undefined }}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selected,
            onChange: (keys) => setSelected(keys),
            columnWidth: 48,
          }}
          onRow={(record) => ({
            onClick: (e) => {
              if (e.target.closest(".ant-checkbox-wrapper, .ant-checkbox")) return;
              setEditId(record.id);
            },
            style: { cursor: "pointer" },
          })}
          locale={{ emptyText: checkedOnce ? "Нет некорректных записей" : "Запустите проверку" }}
        />
      </div>

      {editId && (
        <ServiceMonitoringAbonModal
          saleId={editId}
          onClose={(changed) => {
            setEditId(null);
            if (changed) onRunCheck();
          }}
        />
      )}
    </div>
  );
};

export default ServiceMonitoring;
